import { useMemo, useState } from "react";
import { sendDate, type KeyDateRef, type Sequence, type Touch } from "./emailSmsTypes";
import { KEY_DATE_NAMES, parseDay } from "./emailSmsTypes";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/** Chips per cell before the rest collapse behind "+N more". */
const CHIP_CAP = 3;

type Props = {
  sequences: Sequence[];
  keyDates: KeyDateRef[];
  onOpenTouch: (touchId: string) => void;
};

type Entry = { touch: Touch; sequence: Sequence };

const dayKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/*
    Touches from sequences that are on, placed on the dates they resolve to. Triggers are
    changed in List; this view is for seeing collisions — two asks landing on one day — which
    a list of offsets hides.
*/
export function SequencesCalendar({ sequences, keyDates, onOpenTouch }: Props) {
  const [monthOffset, setMonthOffset] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  const entries = useMemo(() => {
    const map = new Map<string, Entry[]>();
    sequences.forEach((sequence) =>
      sequence.touches.forEach((touch) => {
        if (touch.on === false) return;
        const when = sendDate(touch, keyDates);
        if (!when) return;
        const key = dayKey(when);
        map.set(key, [...(map.get(key) ?? []), { touch, sequence }]);
      }),
    );
    return map;
  }, [sequences, keyDates]);

  const keyDatesByDay = useMemo(() => {
    const map = new Map<string, string[]>();
    keyDates.forEach((keyDate) => {
      if (!keyDate.date) return;
      const when = parseDay(keyDate.date);
      if (!when) return;
      const key = dayKey(when);
      const name = keyDate.label?.trim() || KEY_DATE_NAMES[keyDate.type] || keyDate.type;
      map.set(key, [...(map.get(key) ?? []), name]);
    });
    return map;
  }, [keyDates]);

  /*
      Open on the month holding the first scheduled touch rather than today: the campaign's
      sends are typically months out, and landing on an empty current month reads as "nothing
      is scheduled".
  */
  const anchorMonth = useMemo(() => {
    const first = Array.from(entries.keys()).sort()[0];
    const base = first ? new Date(`${first}T00:00:00`) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  }, [entries]);

  const month = new Date(
    anchorMonth.getFullYear(),
    anchorMonth.getMonth() + monthOffset,
    1,
  );

  const cells = useMemo(() => {
    const firstWeekday = month.getDay();
    const start = new Date(month);
    start.setDate(start.getDate() - firstWeekday);
    // Six weeks always, so the grid does not change height month to month.
    return Array.from({ length: 42 }, (_, i) => {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      return date;
    });
  }, [month]);

  const inMonthCount = cells.reduce(
    (total, date) =>
      date.getMonth() === month.getMonth() ? total + (entries.get(dayKey(date))?.length ?? 0) : total,
    0,
  );

  return (
    <div className="cal">
      <div className="cal__head">
        <button
          type="button"
          className="esms-seq__chev"
          aria-label="Previous month"
          onClick={() => setMonthOffset((v) => v - 1)}
        >
          <i className="bi bi-chevron-left" />
        </button>
        <span className="cal__title">
          {month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        </span>
        <button
          type="button"
          className="esms-seq__chev"
          aria-label="Next month"
          onClick={() => setMonthOffset((v) => v + 1)}
        >
          <i className="bi bi-chevron-right" />
        </button>
        <span className="cal__count">
          {inMonthCount} {inMonthCount === 1 ? "touch" : "touches"} this month
        </span>
        <span className="esms__spacer" />
        <span className="cal__legend">
          <span className="cal__diamond" />
          Key date
        </span>
        <span className="touch-ch touch-ch--email">Email</span>
        <span className="touch-ch touch-ch--sms">SMS</span>
      </div>

      <div className="cal__scroll">
        <div className="cal__grid-wrap">
          <div className="cal__dow">
            {DOW.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>
          <div className="cal__grid">
            {cells.map((date) => {
              const key = dayKey(date);
              const dayEntries = entries.get(key) ?? [];
              const dayKeyDates = keyDatesByDay.get(key) ?? [];
              const outside = date.getMonth() !== month.getMonth();
              const open = expanded === key;
              const shown = open ? dayEntries : dayEntries.slice(0, CHIP_CAP);
              const hidden = dayEntries.length - shown.length;
              return (
                <div className={`cal__cell${outside ? " cal__cell--outside" : ""}`} key={key}>
                  <span className="cal__num">{date.getDate()}</span>

                  {dayKeyDates.map((name) => (
                    <span className="cal__key" key={name}>
                      <span className="cal__diamond" />
                      <span className="cal__key-name">{name}</span>
                    </span>
                  ))}

                  {shown.map(({ touch, sequence }) => (
                    <button
                      type="button"
                      key={touch.id}
                      className="cal__chip"
                      title={`${sequence.name} · ${touch.title}`}
                      onClick={() => onOpenTouch(touch.id)}
                    >
                      <span className={`touch-ch touch-ch--${touch.ch}`}>
                        {touch.ch === "email" ? "Email" : "SMS"}
                      </span>
                      <span className="cal__chip-label">{touch.title}</span>
                    </button>
                  ))}

                  {hidden > 0 && (
                    <button
                      type="button"
                      className="cal__more"
                      onClick={() => setExpanded(key)}
                    >
                      +{hidden} more
                    </button>
                  )}
                  {open && dayEntries.length > CHIP_CAP && (
                    <button
                      type="button"
                      className="cal__more"
                      onClick={() => setExpanded(null)}
                    >
                      Show fewer
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="cal__foot">
        Touches from sequences that are on, on their send dates. Select a touch to preview it.
        Change triggers in List.
      </div>
    </div>
  );
}

export default SequencesCalendar;
